import { useState, useEffect, useRef } from 'react';
import VoiplyHeader from '../components/VoiplyHeader.jsx';
import VoiplyFooter from '../components/VoiplyFooter.jsx';
import ClipPlayerOverlay from '../components/ClipPlayerOverlay.jsx';

// Stream customer subdomain — same one used across Record.jsx / the worker.
// Part of every public Stream URL, not secret.
const STREAM_SUBDOMAIN = 'customer-zzob661ztw2o5t2h';

// Build the clip-shaped object ClipPlayerOverlay expects from a Stream UID.
const landingClip = (uid) => ({
  iframe_url: `https://${STREAM_SUBDOMAIN}.cloudflarestream.com/${uid}/iframe?preload=metadata`,
  thumbnail_url: `https://${STREAM_SUBDOMAIN}.cloudflarestream.com/${uid}/thumbnails/thumbnail.jpg?time=2s&height=400`,
  watch_url: `https://${STREAM_SUBDOMAIN}.cloudflarestream.com/${uid}/watch`,
});

// Format a full name as "First L." for public display — first name plus a
// last initial. Falls back to just the first name when there's no last name.
const firstNameLastInitial = (full) => {
  const parts = String(full || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
};

// Real approved customer testimonials featured on the public landing page.
// Swap in real pull-quotes from the videos when ready.
const LANDING_TESTIMONIALS = [
  {
    name: firstNameLastInitial('Dan'),
    title: 'Why I switched to Voiply',
    blurb: 'What finally made the switch worth it, in his own words.',
    duration: '0:55',
    clip: landingClip('24aeb059bb7b2ea70cd933f9c0dee08a'),
  },
  {
    name: firstNameLastInitial('Taylor Gray'),
    title: 'My Voiply story',
    blurb: 'Why he made the call, and what changed after.',
    duration: '0:41',
    clip: landingClip('278346403cfbf50b0a589ea7ff2bc7de'),
  },
];

// Landing page — what visitors see at the bare domain (no /r/:token).
//
// Two surfaces:
//   1. Marketing-style hero (left text, right floating gift cards)
//   2. Magic-link request card: customer types their 10-digit Voiply
//      landline number. We POST to /api/landing/request-link, which
//      looks them up in HubSpot via the custom number object and emails
//      the magic link to every contact email on file. Worker uses
//      HubSpot's transactional single-email API (template 213062522201)
//      with {{ custom.link }} + {{ contact.firstname }}.
//
// Phone is the right input because:
//   - Boomers know their Voiply landline number cold (it's printed on
//     their bills and on Voiply marketing material).
//   - Email addresses get mistyped constantly — losing a "magic link
//     to your inbox" because the customer typed gmial.com is bad UX.
//   - The progress bar / autosubmit-at-10-digits flow is much friendlier
//     than free-form email validation.
//
// The worker's response is honest, not anti-enumeration (the Voiply
// customer base is small enough that "we couldn't find you" is more
// useful than hiding existence — same trade voiplyportal makes).

export default function Landing() {
  const [phone, setPhone] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | sent | not_found | error
  const [errorMsg, setErrorMsg] = useState('');
  const [maskedEmails, setMaskedEmails] = useState([]);
  const [playingClip, setPlayingClip] = useState(null);
  const inputRef = useRef(null);

  // Format the input as the customer types — (XXX) XXX-XXXX
  // Strip non-digits, cap at 10, then pad with parens/dash as digits land.
  const fmt = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };

  const digits = phone.replace(/\D/g, '');
  const valid = digits.length === 10;

  const submit = async (e) => {
    e?.preventDefault();
    if (!valid || state === 'sending') return;
    setState('sending');
    setErrorMsg('');
    try {
      const res = await fetch('/api/landing/request-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: digits }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setMaskedEmails(data.masked_emails || []);
        setState('sent');
      } else if (data.not_found) {
        setState('not_found');
        setErrorMsg(data.error || "We couldn't find a Voiply account with that number.");
      } else {
        setState('error');
        setErrorMsg(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setState('error');
      setErrorMsg('Network hiccup — please try again.');
    }
  };

  // Autosubmit when 10 digits land — same friendly pattern voiplyportal uses.
  // Short delay so a customer who pastes a number with garbage on the end
  // (some address books store ' x123' extensions) gets a moment to adjust.
  useEffect(() => {
    if (valid && state === 'idle') {
      const id = setTimeout(() => submit(), 240);
      return () => clearTimeout(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid, state]);

  return (
    <div className="vt-app min-h-screen flex flex-col">
      <VoiplyHeader />
      <main className="vt-landing-page flex-1 w-full max-w-[880px] mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Hero — text left, floating gift cards right */}
        <div className="vt-landing-hero animate-fade-in-up">
          <div className="vt-landing-hero-content">
            <div className="vt-eyebrow"><span className="vt-bar"></span>For Voiply customers</div>
            <h1 className="vt-h-display vt-landing-title">
              Tell us your Voiply story.<br />
              <span className="vt-landing-title-accent">We'll send a gift card.</span>
            </h1>
            <p className="vt-lead vt-landing-lede">
              Up to four short videos, recorded right here in your browser. About 15 minutes total.
              You earn <strong className="vt-muted-strong">$10</strong> for each one we accept, plus a{' '}
              <strong className="vt-muted-strong">$20</strong> bonus per video we use in our ads.
            </p>
          </div>

          <div className="vt-landing-hero-art" aria-hidden="true">
            <div className="vt-gift-card vt-gc-1">
              <div className="vt-gc-brand">amazon</div>
              <div className="vt-gc-amount">$10</div>
            </div>
            <div className="vt-gift-card vt-gc-2">
              <div className="vt-gc-brand">VISA</div>
              <div className="vt-gc-amount">$20</div>
            </div>
            <div className="vt-gift-card vt-gc-3">
              <div className="vt-gc-brand">Target</div>
              <div className="vt-gc-amount">$10</div>
            </div>
            <div className="vt-hero-glow"></div>
          </div>
        </div>

        {/* Magic-link card */}
        <div className="vt-paper vt-magic-card animate-fade-in-up" style={{ animationDelay: '120ms' }}>
          {state !== 'sent' ? (
            <>
              <div className="vt-magic-icon-tile" aria-hidden="true">
                {/* Phone icon — matches the "type your landline number" message */}
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
              </div>

              <h2 className="vt-magic-title">Get your personal link</h2>
              <p className="vt-magic-sub">
                Enter your Voiply landline number. We'll email your personal link to the address on file.
              </p>

              <form className="vt-magic-form" onSubmit={submit} noValidate>
                <div className="vt-phone-input-wrapper">
                  <input
                    ref={inputRef}
                    className={'vt-phone-main-input' + (valid ? ' complete' : '')}
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    placeholder="(555) 867-5309"
                    value={phone}
                    onChange={(e) => {
                      setPhone(fmt(e.target.value));
                      // Reset error states the moment they edit
                      if (state === 'not_found' || state === 'error') {
                        setState('idle');
                        setErrorMsg('');
                      }
                    }}
                    disabled={state === 'sending'}
                  />
                  <div className="vt-phone-progress-track">
                    <span className="vt-phone-progress-bar" style={{ width: `${(digits.length / 10) * 100}%` }}></span>
                  </div>
                  {digits.length > 0 && !valid && (
                    <div className="vt-phone-counter">
                      {digits.length}<span className="vt-phone-counter-total">/10</span>
                    </div>
                  )}
                  {valid && state !== 'sending' && (
                    <div className="vt-phone-check" aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className="vt-btn vt-btn-primary vt-btn-lg vt-tap vt-magic-submit"
                  disabled={!valid || state === 'sending'}
                >
                  {state === 'sending' ? (
                    <>
                      <span className="vt-submit-spinner" aria-hidden="true"></span>
                      Looking you up…
                    </>
                  ) : (
                    <>
                      Continue
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="9 5 16 12 9 19"/>
                      </svg>
                    </>
                  )}
                </button>

                {(state === 'not_found' || state === 'error') && errorMsg && (
                  <div className="vt-magic-error">{errorMsg}</div>
                )}
              </form>
            </>
          ) : (
            <div className="vt-magic-success">
              <span className="vt-magic-success-ico">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="32" height="32">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </span>
              <h2 className="vt-magic-title" style={{ margin: '6px 0' }}>Check your email in a minute.</h2>
              <p className="vt-magic-sub">
                We sent your personal link to the email{maskedEmails.length > 1 ? ' addresses' : ''} on file for <strong>{phone}</strong>.
                Tap it from your inbox and you'll come right back here, ready to record.
              </p>
              {maskedEmails.length > 0 && (
                <ul className="vt-magic-email-list">
                  {maskedEmails.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
              <button
                type="button"
                className="vt-link-button vt-tap"
                onClick={() => {
                  setState('idle');
                  setPhone('');
                  setMaskedEmails([]);
                  setErrorMsg('');
                  setTimeout(() => inputRef.current?.focus(), 50);
                }}
              >
                Use a different number
              </button>
            </div>
          )}
        </div>

        {/* Real customer testimonials — reuses the warm/cool example-card
            styling from the welcome strip so it feels native to the brand.
            Plays back via the shared ClipPlayerOverlay (Stream /iframe). */}
        <div className="vt-examples-strip animate-fade-in-up" style={{ animationDelay: '160ms' }}>
          <div className="vt-examples-head">
            <div className="vt-eyebrow" style={{ justifyContent: 'center' }}>
              <span className="vt-bar"></span>Hear from real customers
            </div>
            <h3 className="vt-examples-title">See how others told their story</h3>
            <p className="vt-examples-sub">Real Voiply customers. About a minute each.</p>
          </div>
          <div className="vt-examples-row">
            {LANDING_TESTIMONIALS.map((ex, i) => (
              <button
                key={ex.title}
                type="button"
                className="vt-example-card vt-tap"
                onClick={() => setPlayingClip(ex.clip)}
                aria-label={`Play testimonial: ${ex.title}`}
              >
                <span className="vt-example-thumb" data-shade={i === 0 ? 'warm' : 'cool'}>
                  <img
                    className="vt-example-poster"
                    src={ex.clip.thumbnail_url}
                    alt=""
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <span className="vt-example-portrait" aria-hidden="true">
                    <span className="vt-ep-head"></span>
                    <span className="vt-ep-shoulders"></span>
                  </span>
                  <span className="vt-example-play">
                    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                      <path d="M6 4l14 8-14 8z" fill="currentColor" />
                    </svg>
                  </span>
                  <span className="vt-example-dur">{ex.duration}</span>
                </span>
                <span className="vt-example-meta">
                  <span className="vt-example-name">{ex.name}</span>
                  <span className="vt-example-quote">{ex.blurb}</span>
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div className="vt-howit-strip animate-fade-in-up" style={{ animationDelay: '200ms' }}>
          <div className="vt-howit-strip-head">
            <div className="vt-eyebrow" style={{ justifyContent: 'center' }}>
              <span className="vt-bar"></span>How it works
            </div>
            <h3 className="vt-howit-strip-title">
              Pick stories, read them out loud, we send a gift card.
            </h3>
          </div>
          <div className="vt-howit">
            <div className="vt-howit-card">
              <div className="vt-howit-num"><span className="vt-howit-n">1</span>Pick</div>
              <div className="vt-howit-title">Choose your stories</div>
              <div className="vt-howit-body">A few short scripts with fill-in-the-blanks. Nothing to memorize.</div>
            </div>
            <div className="vt-howit-card">
              <div className="vt-howit-num"><span className="vt-howit-n">2</span>Record</div>
              <div className="vt-howit-title">Press record, read the words</div>
              <div className="vt-howit-body">A teleprompter sits right beside your camera. Redo as many times as you like.</div>
            </div>
            <div className="vt-howit-card">
              <div className="vt-howit-num"><span className="vt-howit-n">3</span>Get paid</div>
              <div className="vt-howit-title">Gift card by email</div>
              <div className="vt-howit-body">We review within 2 business days. Pick Amazon, Visa, Target, or more.</div>
            </div>
          </div>
        </div>

        <p className="vt-landing-foot">
          Looking for Voiply home phone service? <a href="https://voiply.com">Visit voiply.com</a>.
        </p>
      </main>
      <VoiplyFooter />
      {playingClip && (
        <ClipPlayerOverlay
          clip={playingClip}
          script={{ title: 'Customer testimonial' }}
          onClose={() => setPlayingClip(null)}
        />
      )}
    </div>
  );
}
